import { SectionBoxStyled } from '@OnArk/styles/Form.styled';
import {
  GradientBackground,
  MainStyled,
  SectionStyled,
} from '@OnArk/styles/Layout.styled';

import {
  ComponentProps,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from 'react';

import { MaxWidthStyled, mq } from '@hangar31/rc-layout';
import Callout from '@OnArk/admin/GettingStarted/Callout';
import { WALKTHROUGH_ENABLED } from '@OnArk/admin/GettingStarted/enabled';
import Widget from '@OnArk/admin/GettingStarted/Widget';
import SectionLayout from '@OnArk/admin/Section/SectionLayout';
import { churchCssVars } from '@OnArk/app/bridge/accent';
import Footer from '@OnArk/public/Footer';
import { guestAnalyticsDistinctId } from '@OnArk/utils/guestAnalyticsIdentity';
import posthog from '@OnArk/utils/posthog';
import { Layout } from 'graphql/withAuth';

import AdminNav from './AdminNav';
import GuestNav from './GuestNav';
import SimpleAdminNav from './SimpleNav';

// Hide the new-user walkthrough widget + callout once the church has an
// active Stripe subscription. They can replay anytime from the profile
// page. `billing` is propagated by withAuth from the `me` query.
//
// We require BOTH a Stripe customer on the church AND at least one
// subscription that counts as "started a plan". The customer check matters
// because super users see every subscription on the platform via
// getAllBillingData — without the church.customer gate, super accounts
// looking at a brand-new church would have the widget hidden by some other
// church's subscription.
//
// `trialing` counts: trials begin in Stripe's `trialing` status (the
// card-on-trial signup, see selectPlan), so a church mid-trial has already
// picked a plan and shouldn't keep seeing the onboarding walkthrough.
// `past_due` counts too — they're a paying customer with a late invoice,
// not a brand-new church.
const SUBSCRIBED_STATUSES = new Set(['active', 'trialing', 'past_due']);

function isPayingCustomer(
  church: { customer?: string | null } | undefined,
  billing: { status?: string | null }[] | undefined
) {
  if (!church?.customer) return false;

  return !!billing?.some(b => !!b?.status && SUBSCRIBED_STATUSES.has(b.status));
}

// ——— pageProps typing —————————————————————————————————————————————————————
// Every page funnels its own getServerSideProps result through _app into this
// wrapper, so there is no single accurate type for `pageProps`. What we CAN
// type honestly is (a) the fields LayoutWrapper itself reads — all optional,
// since public/auth pages have none of them — and (b) per layout, the props of
// the chrome components that layout spreads `pageProps` into. The chrome
// types are derived from the components themselves so they can't drift.

/** The fields LayoutWrapper itself reads off any page's props. */
interface LayoutPageProps {
  billing?: { plan?: string | null; status?: string | null }[];
  checkIn?: WithAuthCheckIn;
  church?: WithAuthChurch;
  user?: WithAuthUser;
}

// AdminNav's outer props (withClient already stripped `client`): church + user.
type AdminChromePageProps = LayoutPageProps & ComponentProps<typeof AdminNav>;
// The builder additionally spreads into SectionLayout (the section-page union).
type BuilderChromePageProps = AdminChromePageProps &
  ComponentProps<typeof SectionLayout>;
// Guest pages spread into GuestNav (locale/church/currentSection/course) and
// SectionBoxStyled (all-optional styling props, so it adds no requirements).
type GuestChromePageProps = LayoutPageProps & ComponentProps<typeof GuestNav>;

/** What each layout's renderer can rely on being present in pageProps. */
interface PagePropsByLayout {
  ADMIN: AdminChromePageProps;
  ADMIN_AUTH_SHELL: LayoutPageProps;
  ADMIN_BUILDER: BuilderChromePageProps;
  ADMIN_GRID: AdminChromePageProps;
  ADMIN_NARROW: LayoutPageProps;
  ADMIN_NO_CHURCH: LayoutPageProps;
  AUTH: LayoutPageProps;
  GUEST: GuestChromePageProps;
  PUBLIC: LayoutPageProps;
}

// PostHog identification + capture side effects, split out to keep
// LayoutWrapper simple: identify the logged-in user and/or the checked-in
// guest when their ids are present. Funnel-class guests are keyed by email so
// they merge with the server-side funnel events — see guestAnalyticsDistinctId.
function identifyForPosthog(pageProps: LayoutPageProps) {
  if (pageProps.user?.id) {
    posthog?.identify(
      pageProps.user.id,
      {},
      { ...pageProps.user, currentChurch: pageProps.church }
    );
    posthog?.capture('loggedIn');
  }
  if (pageProps.checkIn?.guest?.email) {
    posthog?.identify(
      guestAnalyticsDistinctId(pageProps.checkIn),
      {},
      pageProps.checkIn.guest
    );
    posthog?.capture('guestLoggedIn');
  }
}

interface LayoutRenderArgs<P = LayoutPageProps> {
  children: ReactNode;
  pageProps: P;
  onboarding: ReactNode;
}

// One render function per layout. Extracting the long if/else-if chain into a
// lookup keeps LayoutWrapper's complexity low; the default (unknown layout)
// case is handled by LayoutWrapper falling back to a plain fragment. Each
// renderer's pageProps is typed to what its layout's chrome actually needs,
// so every `{...pageProps}` spread below is checked against the real
// component props.
const LAYOUT_RENDERERS: {
  [L in Layout]?: (
    args: LayoutRenderArgs<PagePropsByLayout[L]>
  ) => ReactElement;
} = {
  ADMIN: ({ children, onboarding, pageProps }) => (
    <MainStyled grid>
      <AdminNav {...pageProps} />
      <SectionStyled>
        <MaxWidthStyled size="lg">{children}</MaxWidthStyled>
      </SectionStyled>
      {onboarding}
    </MainStyled>
  ),
  ADMIN_AUTH_SHELL: ({ children }) => <>{children}</>,
  ADMIN_BUILDER: ({ children, onboarding, pageProps }) => (
    <MainStyled grid>
      <AdminNav {...pageProps} />
      <SectionLayout {...pageProps}>{children}</SectionLayout>
      {onboarding}
    </MainStyled>
  ),
  ADMIN_GRID: ({ children, onboarding, pageProps }) => (
    <MainStyled grid>
      <AdminNav {...pageProps} />
      {children}
      {onboarding}
    </MainStyled>
  ),
  ADMIN_NO_CHURCH: ({ children }) => (
    <>
      <GradientBackground />
      <SimpleAdminNav state="LOGGED_IN" />
      <MainStyled>{children}</MainStyled>
    </>
  ),
  AUTH: ({ children }) => (
    <>
      <GradientBackground />
      <SimpleAdminNav state="PUBLIC" />
      <main css={mq({ fontSize: ['1.4rem', undefined, '2rem', '2.4rem'] })}>
        {children}
      </main>
      <Footer />
    </>
  ),
  GUEST: ({ children, pageProps }) => (
    // `style` carries the church CSS vars (v2 guest migration proof,
    // docs/v2-guest-builder-incremental-migration.md step 1) so any bridge
    // component under this chrome — TextStep today, more renderers as they
    // port — can read --church-accent/--church-accent-text/
    // --church-button-radius without reaching the Emotion theme.
    // pageProps.church already carries brandingOverride (withAuth), so demo
    // classes wearing another church's brand need no extra work here.
    <SectionBoxStyled {...pageProps} style={churchCssVars(pageProps.church)}>
      <GuestNav {...pageProps} />
      <MainStyled>
        <MaxWidthStyled as="section" css={{ marginTop: '2rem' }} size="sm">
          {children}
        </MaxWidthStyled>
      </MainStyled>
    </SectionBoxStyled>
  ),
};

const LayoutWrapper = ({
  children,
  layout,
  pageProps,
}: PropsWithChildren<{
  pageProps: LayoutPageProps;
  layout: Layout;
}>) => {
  identifyForPosthog(pageProps);

  // Onboarding is admin-only and hidden once the church starts paying.
  // Also gated behind WALKTHROUGH_ENABLED — the guided walkthrough is
  // disabled for now (see GettingStarted/enabled.ts).
  const showOnboarding =
    WALKTHROUGH_ENABLED &&
    !!pageProps.church?.id &&
    !isPayingCustomer(pageProps.church, pageProps.billing);
  const onboarding = showOnboarding ? (
    <>
      <Callout />
      <Widget signals={{}} />
    </>
  ) : null;

  const render = LAYOUT_RENDERERS[layout];

  if (render) {
    // Each page's withAuth wrapper supplies the props its declared layout's
    // chrome needs (an ADMIN page always ships church/user, a GUEST page
    // always ships locale/currentSection, …) — that layout ↔ pageProps
    // correlation can't be expressed across the map lookup, so this single
    // contained assertion re-widens the picked renderer to the fields Layout
    // itself verifies. The per-layout renderer signatures above still keep
    // every JSX spread checked against the real chrome-component props.
    return (render as (args: LayoutRenderArgs) => ReactElement)({
      children,
      onboarding,
      pageProps,
    });
  }

  return <>{children}</>;
};

export default LayoutWrapper;
